import React, {useEffect} from "react";
import {DerivedComponent, FormComponentProps, FormType} from "../../DynamicFormTypes.ts";
import {useFormMethods} from "../../DyanmicFormContext.tsx";
import {useController, useWatch} from "react-hook-form";
import {Checkbox} from "../Checkbox.tsx";
import {useValidationRules} from "../../hooks/useValidationRules.tsx";

/**
 * A checkbox that acts like a radio button across datagrid rows.
 * When checked, it unchecks all sibling rows in the same datagrid.
 *
 * Config example:
 * {
 *   "type": "derived",
 *   "fieldKey": "saleCompMostComparable",
 *   "calculationRule": "MOST_COMPARABLE",
 *   "listFieldKeys": [{ "fieldKey": "saleCompMostComparable", "dataGrid": "datagridconfigurablecompsselectSale" }]
 * }
 */
const ExclusiveCheckboxRenderer: React.FC<FormComponentProps<DerivedComponent>> = ({component}) => {
    const {formMethods} = useFormMethods();
    const {setValue, control} = formMethods;
    const validate = useValidationRules({component});

    const dataGridKey = component.listFieldKeys[0].dataGrid!;
    const fieldSuffix = component.fieldKey.split(".")[2];
    const rowIndex = parseInt(component.fieldKey.split(".")[1]);

    const {field, fieldState} = useController({
        name: component.fieldKey,
        control,
        rules: validate
    });

    const gridData = useWatch({control, name: dataGridKey, defaultValue: []}) as any[];

    useEffect(() => {
        if (field.value !== true) return;

        gridData?.forEach((_row: any, i: number) => {
            if (i === rowIndex) return;
            setValue(`${dataGridKey}.${i}.${fieldSuffix}`, false);
        });
    }, [field.value, rowIndex, dataGridKey, fieldSuffix, gridData?.length]);

    return (
        <Checkbox
            parentController={{field, fieldState}}
            component={{
                fieldKey: component.fieldKey,
                type: FormType.CHECKBOX,
                label: component.label,
                compactUI: component.compactUI,
                validationRules: component.validationRules,
                suffix: component.suffix,
                fieldsToTriggerOnBlur: component.fieldsToTriggerOnBlur,
            }}
        />
    );
};

export const ExclusiveCheckbox = React.memo(ExclusiveCheckboxRenderer);
